import { SATError, SATResult } from "@sadtx/sat-error";
import {
  StoreGuardian,
  StoreOrder,
  StorePatientContext,
  StorePaymentHistoryPage,
  StorePaymentMethodType,
  StorePaymentStatus,
  StoreProduct,
  StoreProductType,
  StorePurchaseResult,
  StoreRefundQuote,
  StoreRefundRequest,
  StoreRefundResult,
  StoreSettlementPage,
  TreatmentVoucherClaim,
  StoreBrandpayCard,
  StoreBrandpayCustomer,
  StorePaymentFlow,
} from "../../domain/entity/store-entities";
import { maskName } from "../../utils/mask-name";
import { getBrowserTimeZone } from "@/utils/date-util";
import { StoreRepository } from "../../domain/repository/store-repository";
import {
  StoreGuardianSearchResponseDto,
  StoreProductResponseDto,
  StorePurchaseResponseDto,
  StoreRefundRequestResponseDto,
} from "../dto/store-response-dtos";
import { StoreSource } from "../source/store-source";

const mapProduct = (dto: StoreProductResponseDto): StoreProduct => ({
  productId: dto.productId,
  type:
    dto.type === StoreProductType.GUARDIAN_APP
      ? StoreProductType.GUARDIAN_APP
      : StoreProductType.TREATMENT,
  months: dto.months,
  name: dto.name,
  description: dto.description,
  price: dto.price,
  originalPrice: dto.originalPrice,
  vatIncluded: dto.vatIncluded,
});

const mapPurchaseResult = (dto: StorePurchaseResponseDto): StorePurchaseResult => ({
  orderId: dto.orderId,
  voucherIssued: dto.voucherIssued,
  receipt: dto.receipt,
  receiptUrl: dto.receiptUrl,
  approvedAt: dto.approvedAt,
});

const mapGuardian = (dto: StoreGuardianSearchResponseDto): StoreGuardian => ({
  guardianUuid: dto.guardianUuid,
  name: dto.name,
  maskedPhone: dto.maskedPhone,
  children: dto.children.map((child) => ({
    patientUuid: child.patientUuid,
    name: child.name,
    birthDate: child.birthDate,
    hasActiveTreatment: child.hasActiveTreatment,
    hasUnusedVoucher: child.hasUnusedVoucher ?? false,
  })),
});

const mapRefundRequest = (dto: StoreRefundRequestResponseDto): StoreRefundRequest => ({
  id: dto.id,
  orderId: dto.orderId,
  hospitalName: dto.hospitalName,
  productName: dto.productName,
  patientNameMasked: dto.patientNameMasked,
  amount: dto.amount,
  reason: dto.reason,
  detail: dto.detail,
  status: dto.status,
  createdAt: dto.createdAt,
  decidedAt: dto.decidedAt,
  decisionNote: dto.decisionNote,
});

export class StoreRepositoryImpl implements StoreRepository {
  constructor(private readonly source: StoreSource) {}

  async fetchProducts(): Promise<SATResult<StoreProduct[], SATError>> {
    const result = await this.source.fetchProducts();
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(result.value.map(mapProduct));
  }

  async fetchBrandpayCustomer(): Promise<SATResult<StoreBrandpayCustomer, SATError>> {
    const result = await this.source.fetchBrandpayCustomer();
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    const dto = result.value;
    return SATResult.success({
      customerKey: dto.customerKey,
      flow: dto.flow,
    });
  }

  async fetchBrandpayCards(): Promise<SATResult<StoreBrandpayCard[], SATError>> {
    const result = await this.source.fetchBrandpayMethods();
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(
      result.value.cards.map((card) => ({
        methodKey: card.methodKey,
        alias: card.alias,
        cardName: card.cardName,
        cardNumber: card.cardNumber,
        cardType: card.cardType,
        ownerType: card.ownerType,
      }))
    );
  }

  async resolvePatientContext(
    patientUuid: string
  ): Promise<SATResult<StorePatientContext, SATError>> {
    const result = await this.source.resolvePatientContext(patientUuid);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success({
      guardianUuid: result.value.guardianUuid,
      guardianName: result.value.guardianName,
      guardianMaskedPhone: result.value.guardianMaskedPhone,
      patientUuid: result.value.patientUuid,
      patientName: result.value.patientName,
      patientBirthDate: result.value.patientBirthDate,
      hasUnusedVoucher: result.value.hasUnusedVoucher,
    });
  }

  async searchGuardian(
    guardianName: string,
    guardianPhone: string
  ): Promise<SATResult<StoreGuardian | null, SATError>> {
    const result = await this.source.searchGuardian({ guardianName, guardianPhone });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(result.value ? mapGuardian(result.value) : null);
  }

  async prepareOrder(
    productId: string,
    patientUuid: string,
    patientName: string,
    paymentFlow: StorePaymentFlow
  ): Promise<SATResult<StoreOrder, SATError>> {
    // 원본 환자 이름은 스토어 서버로 보내지 않는다 — 마스킹 스냅샷만 전달 (병원은 토큰에서 서버가 결정)
    const result = await this.source.prepareOrder({
      productId,
      patientUuid,
      patientNameMasked: maskName(patientName),
      timeZone: getBrowserTimeZone(),
      paymentFlow,
    });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success({
      orderId: result.value.orderId,
      orderName: result.value.orderName,
      supplyAmount: result.value.supplyAmount ?? null,
      vatAmount: result.value.vatAmount ?? null,
      amount: result.value.amount,
      currency: "KRW",
    });
  }

  async confirmPurchase(
    paymentKey: string,
    orderId: string,
    amount: number,
    pgProvider: string
  ): Promise<SATResult<StorePurchaseResult, SATError>> {
    const result = await this.source.confirmPurchase({
      paymentKey,
      orderId,
      amount,
      pgProvider,
    });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(mapPurchaseResult(result.value));
  }

  async claimTreatmentVoucher(
    orderId: string,
    receipt: string
  ): Promise<SATResult<TreatmentVoucherClaim, SATError>> {
    const result = await this.source.claimTreatmentVoucher({ receipt });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    // 표시용 수령 표시 — 실패해도 자격 발급 자체는 완료된 상태
    await this.source.markOrderClaimed(orderId);
    return SATResult.success({
      voucherUuid: result.value.voucherUuid,
      orderId: result.value.orderId,
      months: result.value.months,
      alreadyClaimed: result.value.alreadyClaimed,
    });
  }

  async reclaimOrder(orderId: string): Promise<SATResult<TreatmentVoucherClaim, SATError>> {
    const receiptResult = await this.source.fetchOrderReceipt(orderId);
    if (receiptResult.isFailure) {
      return SATResult.failure(receiptResult.error);
    }
    return this.claimTreatmentVoucher(orderId, receiptResult.value.receipt);
  }

  async prepareRefund(orderId: string): Promise<SATResult<StoreRefundQuote, SATError>> {
    // 1. 의료 서버: 사용 여부·공급 정보 확인 + 잠금 + 증서
    const attestationResult = await this.source.requestVoucherRefundAttestation({ orderId });
    if (attestationResult.isFailure) {
      return SATResult.failure(attestationResult.error);
    }
    const attestation = attestationResult.value.attestation;
    // 2. store: 규칙·금액 견적 (부작용 없음). 실패하면 잠금 해제
    const quoteResult = await this.source.quoteRefund(orderId, { attestation });
    if (quoteResult.isFailure) {
      await this.source.cancelVoucherRefund({ orderId });
      return SATResult.failure(quoteResult.error);
    }
    return SATResult.success({ ...quoteResult.value, attestation });
  }

  async executeRefund(
    orderId: string,
    attestation: string
  ): Promise<SATResult<StoreRefundResult, SATError>> {
    // 3. store: 증서 검증 → PG 취소(부분 가능) → 환불 영수증
    const refundResult = await this.source.refundOrder(orderId, { attestation });
    if (refundResult.isFailure) {
      // 돈이 움직이지 않았으므로 잠금만 풀어 이용권을 원상 복구
      await this.source.cancelVoucherRefund({ orderId });
      return SATResult.failure(refundResult.error);
    }
    // 4. 의료 서버: 환불 영수증으로 자격 회수(활성 치료면 종료) — 실패해도 환불은 완료된 상태
    const revokeResult = await this.source.revokeTreatmentVoucher({
      refundReceipt: refundResult.value.refundReceipt,
    });
    return SATResult.success({
      orderId,
      refundedAt: refundResult.value.refundedAt,
      amount: refundResult.value.amount,
      refundType: refundResult.value.refundType,
      voucherRevoked: revokeResult.isSuccess,
    });
  }

  async cancelRefund(orderId: string): Promise<SATResult<void, SATError>> {
    return this.source.cancelVoucherRefund({ orderId });
  }

  async requestExceptionRefund(
    orderId: string,
    reason: string,
    detail: string
  ): Promise<SATResult<StoreRefundRequest, SATError>> {
    const result = await this.source.createRefundRequest(orderId, {
      reason: reason as "DEVICE_DEFECT" | "DOCTOR_STOP" | "SERIOUS_CONDITION" | "TECHNICAL" | "OTHER",
      detail,
    });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(mapRefundRequest(result.value));
  }

  async fetchRefundRequests(status: string | null): Promise<SATResult<StoreRefundRequest[], SATError>> {
    const result = await this.source.fetchRefundRequests(status);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(result.value.map(mapRefundRequest));
  }

  async decideRefundRequest(
    requestId: number,
    decision: "APPROVE" | "REJECT",
    note: string | null
  ): Promise<SATResult<StoreRefundRequest, SATError>> {
    const result = await this.source.decideRefundRequest(requestId, {
      decision,
      ...(note ? { note } : {}),
    });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(mapRefundRequest(result.value));
  }

  async fetchPaymentHistory(
    page: number,
    size: number,
    status: string | null,
    query: string | null
  ): Promise<SATResult<StorePaymentHistoryPage, SATError>> {
    const result = await this.source.fetchPaymentHistory({ page, size, status, query });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    const dto = result.value;
    return SATResult.success({
      hospitalName: dto.hospitalName,
      items: dto.items.map((item) => ({
        orderId: item.orderId,
        paidAt: item.paidAt,
        timeZone: item.timeZone,
        patientNameMasked: item.patientNameMasked,
        productName: item.productName,
        months: item.months,
        amount: item.amount,
        method: (item.method === "SAVED_CARD" ? "SAVED_CARD" : "CARD") as StorePaymentMethodType,
        status: (["PAID", "REFUNDED", "CANCELED"].includes(item.status)
          ? item.status
          : "PAID") as StorePaymentStatus,
        receiptUrl: item.receiptUrl,
        claimedAt: item.claimedAt ?? null,
        refundedAt: item.refundedAt ?? null,
        refundAmount: item.refundAmount ?? null,
        refundType: item.refundType ?? null,
        refundable: item.refundable ?? false,
        exceptionApproved: item.exceptionApproved ?? false,
        refundRequest: item.refundRequest ?? null,
      })),
      page: dto.page,
      totalPages: dto.totalPages,
      totalCount: dto.totalCount,
      summary: dto.summary,
    });
  }

  async fetchSettlements(month: string): Promise<SATResult<StoreSettlementPage, SATError>> {
    const result = await this.source.fetchSettlements({ month });
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(result.value);
  }
}
